/**
 * Store Enforcement Utilities
 * 
 * Helper functions for integrating policy-based authorization
 * into Zustand store mutations.
 * 
 * These utilities wrap the core enforcement functions with
 * patterns optimized for store usage.
 * 
 * Usage in stores:
 * 
 * updateCandidate: (id, updates) => {
 *   const candidate = get().candidates.find(c => c.id === id);
 *   if (!candidate) return;
 *   
 *   // Enforce policy - throws if denied
 *   enforcePolicyForItem('items.edit.content', id, candidate);
 *   
 *   // Proceed with update
 *   set((state) => ({ candidates: state.candidates.map(c => c.id === id ? {...c, ...updates} : c) }));
 * }
 */

import {
  enforceAuthorization,
  checkAuthorization,
  buildResourceContext,
  isAuthorizationError,
  getAuthorizationErrorMessage,
} from './enforce';
import type { ResourceType, ResourceContext, EvaluationContext, PolicyDecision } from './types';
import { useCycleProgressStore } from '../../stores/prioritization/cycleProgressStore';
import { useTenantStore } from '../../stores/prioritization/tenantStore';
import { getSafeSupabaseClient, getCurrentWorkspaceId } from '../supabaseSync';
import { getResolvedCapabilitiesForUser } from '../permissionResolver';
import { useUserStore } from '../../stores/prioritization/userStore';
import { getCapabilitiesForAction } from '../../types/prioritization/capabilityIds';
import type { PermissionAction } from '../../types/prioritization/permissions';

// ============================================================================
// DB-Direct Capability Check (bypasses Zustand cache)
// ============================================================================

/**
 * Check whether the current user has a capability by reading from the DB
 * (via permissionResolver's TTL-cached role_assignments + fresh scope evaluation).
 *
 * Use this for authority-creating actions (§2.3a) where Zustand's in-memory
 * resolvedCapabilityIds cannot be trusted as the authoritative gate — it may
 * be empty due to load-order races, stale due to role changes, or loaded with
 * the wrong scope. The DB is always authoritative.
 */
async function hasCapabilityFromDB(action: PermissionAction): Promise<boolean> {
  const userId = useUserStore.getState().userId;
  if (!userId || userId === 'unknown') return false;
  const workspaceId =
    useTenantStore.getState().currentWorkspaceId ?? getCurrentWorkspaceId() ?? undefined;
  const scope = workspaceId
    ? { kind: 'resource' as const, id: workspaceId, parentScope: { kind: 'organization' as const, id: '*' } }
    : { kind: 'organization' as const, id: '*' };
  const caps = await getResolvedCapabilitiesForUser(userId, scope);
  const required = getCapabilitiesForAction(action);
  return required.some((cap) => caps.includes(cap));
}

// ============================================================================
// Ask again after rights load (Roadmap Q-12)
// ============================================================================

/**
 * Ask a permission question; if the answer is no, load the person's rights and
 * ask once more before saying no.
 *
 * `userStore.can` falls back to the current role's baseline until
 * `permissionsLoaded` is true, so a click made soon after a page load can be
 * refused for someone who holds the right. A refusal is therefore only final
 * after the rights have been loaded and the same question asked again.
 *
 * `ask` is called again, so it must read the store fresh each time (not close
 * over a snapshot of it). It may answer with a boolean or a PolicyDecision.
 *
 * Any store act that refuses on a policy answer goes through this, so the
 * "load, then ask again" step lives in one place.
 */
export async function askAgainAfterPermissionsLoad<T extends boolean | PolicyDecision>(
  ask: () => T,
): Promise<T> {
  const isYes = (answer: T): boolean => (typeof answer === 'boolean' ? answer : answer.allowed);
  const first = ask();
  if (isYes(first)) return first;
  await useUserStore.getState().loadPermissions();
  return ask();
}

// ============================================================================
// Store-Friendly Enforcement
// ============================================================================

/**
 * Enforce policy for an item mutation.
 * Throws AuthorizationError if denied.
 */
export function enforcePolicyForItem(
  action: string,
  itemId: string,
  itemData: Record<string, unknown>,
  context?: Partial<EvaluationContext>
): PolicyDecision {
  const resource = buildResourceContext('item', itemId, itemData);
  return enforceAuthorization(action, resource, context);
}

/**
 * Enforce policy for a list mutation.
 * Throws AuthorizationError if denied.
 */
export function enforcePolicyForList(
  action: string,
  listId: string,
  listData: Record<string, unknown>,
  context?: Partial<EvaluationContext>
): PolicyDecision {
  const resource = buildResourceContext('list', listId, listData);
  return enforceAuthorization(action, resource, context);
}

/**
 * Enforce policy for a session mutation.
 * Throws AuthorizationError if denied.
 */
export function enforcePolicyForSession(
  action: string,
  sessionId: string,
  sessionData: Record<string, unknown>,
  context?: Partial<EvaluationContext>
): PolicyDecision {
  const resource = buildResourceContext('session', sessionId, sessionData);
  return enforceAuthorization(action, resource, context);
}

/**
 * Enforce policy for a cycle/process mutation.
 * Throws AuthorizationError if denied.
 */
export function enforcePolicyForCycle(
  action: string,
  cycleId: string,
  cycleData: Record<string, unknown>,
  context?: Partial<EvaluationContext>
): PolicyDecision {
  const resource = buildResourceContext('cycle', cycleId, cycleData);
  return enforceAuthorization(action, resource, context);
}

/**
 * Enforce policy for a generic resource.
 * Throws AuthorizationError if denied.
 */
export function enforcePolicyForResource(
  action: string,
  resourceType: ResourceType,
  resourceId: string,
  resourceData: Record<string, unknown>,
  context?: Partial<EvaluationContext>
): PolicyDecision {
  const resource = buildResourceContext(resourceType, resourceId, resourceData);
  return enforceAuthorization(action, resource, context);
}

// ============================================================================
// Soft Enforcement (Check Without Throwing)
// ============================================================================

/**
 * Normalize item/candidate data so the evaluator always receives workspaceId, createdBy, ownerId, assigneeId, state.
 * Handles both camelCase and snake_case so policy predicates see consistent fields.
 */
function normalizeItemDataForPolicy(itemData: Record<string, unknown>): Record<string, unknown> {
  const createdBy =
    (itemData.createdBy as string) ??
    (itemData.createdByUserId as string) ??
    (itemData.created_by_user_id as string) ??
    (itemData.created_by as string);
  const assigneeId =
    (itemData.assigneeId as string) ??
    (itemData.shapingOwnerId as string) ??
    (itemData.shaping_owner_id as string) ??
    (itemData.stewardId as string) ??
    (itemData.steward_id as string);
  const ownerId = (itemData.ownerId as string) ?? (itemData.owner_id as string);
  const workspaceId = (itemData.workspaceId as string) ?? (itemData.workspace_id as string);
  const lifecycleState =
    (itemData.lifecycleState as string) ??
    (itemData.lifecycle_state as string) ??
    'item';
  const exitStatus =
    (itemData.exit_status as string) ??
    (itemData.detailedState as string) ??
    (itemData.detailed_state as string) ??
    'active';
  return {
    ...itemData,
    createdBy,
    assigneeId,
    ownerId,
    workspaceId,
    lifecycleState,
    exit_status: exitStatus,
    detailedState: exitStatus, // backward compat alias
  };
}

/**
 * Check policy for an item without throwing.
 * Normalizes itemData so the evaluator always gets workspaceId, createdBy, ownerId, assigneeId, state.
 * Returns the decision for conditional logic.
 */
export function checkPolicyForItem(
  action: string,
  itemId: string,
  itemData: Record<string, unknown>,
  context?: Partial<EvaluationContext>
): PolicyDecision {
  const normalized = normalizeItemDataForPolicy(itemData);
  const resource = buildResourceContext('item', itemId, normalized as Parameters<typeof buildResourceContext>[2]);
  return checkAuthorization(action, resource, context);
}

/**
 * Check policy for a list without throwing.
 */
/**
 * Check policy for a list/pool action without throwing.
 *
 * Sets workspace scope on the resource — same reasoning as checkPolicyForSession.
 */
export function checkPolicyForList(
  action: string,
  listId: string,
  listData: Record<string, unknown>,
  context?: Partial<EvaluationContext>
): PolicyDecision {
  const workspaceId =
    (listData.workspaceId as string | undefined) ??
    (listData.workspace_id as string | undefined) ??
    useTenantStore.getState().currentWorkspaceId ??
    getCurrentWorkspaceId() ??
    undefined;
  const workspaceScope =
    workspaceId != null
      ? { kind: 'resource' as const, id: workspaceId, parentScope: { kind: 'organization' as const, id: '*' } }
      : undefined;
  const normalizedListData = {
    ...listData,
    ...(workspaceId != null && { workspaceId }),
    ...(workspaceScope != null && { scope: workspaceScope }),
  };
  const resource = buildResourceContext('list', listId, normalizedListData);
  return checkAuthorization(action, resource, context);
}

/**
 * Returns true if any required decision loop (upstream of item_prioritization) is not COMPLETE.
 * Used during Step 5 prerequisite evaluation for force_rank intent.
 */
async function fetchDecisionLoopsIncomplete(cycleId: string): Promise<boolean> {
  const supabase = getSafeSupabaseClient();
  if (!supabase) return false;
  const { data, error } = await supabase
    .from('cycle_decision_loops')
    .select('id')
    .eq('cycle_id', cycleId)
    .in('decision_class_id', [
      'item_selection',
      'attribute_selection',
      'values_choice_selection',
      'value_selection',
    ])
    .neq('status', 'COMPLETE')
    .limit(1);
  if (error) return false;
  return (data?.length ?? 0) > 0;
}

/**
 * Build prerequisites for cycle pool actions from cycleData and/or cycle progress.
 * Sets only cyclePoolLocked (decomposed); does not set legacy poolLocked.
 */
function buildCyclePrerequisites(
  cycleId: string,
  cycleData: Record<string, unknown>
): Partial<EvaluationContext>['prerequisites'] {
  const fromData =
    typeof cycleData.cyclePoolLocked === 'boolean'
      ? cycleData.cyclePoolLocked
      : typeof cycleData.pool_locked === 'boolean'
        ? cycleData.pool_locked
        : undefined;
  const progress = useCycleProgressStore.getState().getProgress(cycleId);
  const fromProgress = progress?.poolLocked;
  const cyclePoolLocked = fromData ?? (typeof fromProgress === 'boolean' ? fromProgress : undefined);
  if (cyclePoolLocked === undefined) return undefined;
  return { cyclePoolLocked };
}

/**
 * Build round/proposal prerequisites from cycleData when present.
 * Sets only roundProposalLocked; does not set legacy poolLocked.
 */
function buildRoundProposalPrerequisites(
  _cycleId: string,
  cycleData: Record<string, unknown>
): Partial<EvaluationContext>['prerequisites'] {
  const roundProposalLocked =
    typeof cycleData.roundProposalLocked === 'boolean'
      ? cycleData.roundProposalLocked
      : typeof cycleData.round_proposal_locked === 'boolean'
        ? cycleData.round_proposal_locked
        : undefined;
  if (roundProposalLocked === undefined) return undefined;
  return { roundProposalLocked };
}

/**
 * Check policy for a workflow (edit config, set default, create, archive).
 * Resource is workflow scope; data must include workspaceId for workspace-scoped rules.
 */
export function checkPolicyForWorkflow(
  action: string,
  workflowIdOrScope: string,
  data: Record<string, unknown>,
  context?: Partial<EvaluationContext>
): PolicyDecision {
  const workspaceId = data.workspaceId ?? data.workspace_id ?? useTenantStore.getState().currentWorkspaceId ?? getCurrentWorkspaceId();
  const normalized = { ...data, workspaceId: workspaceId ?? undefined };
  const resource = buildResourceContext('workflow' as ResourceType, workflowIdOrScope, normalized);
  return checkAuthorization(action, resource, context);
}

/**
 * Check policy for a cycle without throwing.
 * When action is process.prioritize (force_rank), runs decision-loop prerequisite query and merges result into context.
 * Ensures cycle resource has workspaceId (fallback to current workspace) so workspace-scoped allow rules can match.
 */
// Actions that create or mutate governance artifacts on a cycle — checked
// against the DB directly (§2.3a) rather than against Zustand's in-memory
// capability cache, which can be stale or race-condition-empty at call time.
// All entries must have a mapping in PERMISSION_ACTION_TO_CAPABILITIES.
const CYCLE_DB_CHECKED_ACTIONS = new Set([
  'process.create',          // create a new cycle
  'process.edit',            // mutate cycle configuration
  'criteria.edit',           // edit criteria on a cycle
  'cycle.pool.add',          // add items to cycle pool
  'cycle.pool.remove',       // remove items from cycle pool
  'tracks.create',           // add a track to a cycle
  'track.set_official_result', // record official results
  'session.publish',         // publish a session (governance outcome)
]);

/**
 * Build a PolicyDecision straight from the DB-authoritative capability check
 * (§2.3a) — no resource, no Zustand cache. Shared by checkPolicyForCycle's
 * CYCLE_DB_CHECKED_ACTIONS branch and checkPolicyForTrackCreate below.
 */
async function dbCapabilityDecision(action: PermissionAction): Promise<PolicyDecision> {
  const allowed = await hasCapabilityFromDB(action);
  if (!allowed) {
    return {
      allowed: false,
      decision: 'deny',
      ruleId: 'db-capability-check',
      reasons: [{ code: 'MISSING_CAPABILITY', data: { permission: action } }],
    };
  }
  return {
    allowed: true,
    decision: 'allow',
    ruleId: 'db-capability-check',
    reasons: [{ code: 'DEFAULT_ALLOW', data: {} }],
  };
}

/**
 * Check the tracks.create capability directly against the DB (§2.3a).
 * Standalone (Unbound) tracks have no parent cycle, so this is exposed
 * outside checkPolicyForCycle — which requires a cycleId — rather than
 * forcing every caller to fabricate one.
 */
export async function checkPolicyForTrackCreate(): Promise<PolicyDecision> {
  return dbCapabilityDecision('tracks.create');
}

export async function checkPolicyForCycle(
  action: string,
  cycleId: string,
  cycleData: Record<string, unknown>,
  context?: Partial<EvaluationContext>
): Promise<PolicyDecision> {
  // For authority-creating actions, verify the capability directly from the DB
  // rather than from Zustand. This is the build-contract-correct enforcement
  // point — client-side in-memory state is never authoritative (§2.3a).
  if (CYCLE_DB_CHECKED_ACTIONS.has(action)) {
    return dbCapabilityDecision(action as PermissionAction);
  }

  const workspaceId =
    cycleData.workspaceId ?? cycleData.workspace_id ?? useTenantStore.getState().currentWorkspaceId ?? undefined;
  const workspaceScope =
    workspaceId != null
      ? { kind: 'resource' as const, id: workspaceId, parentScope: { kind: 'organization' as const, id: '*' } }
      : undefined;
  const normalizedCycleData = {
    ...cycleData,
    ...(workspaceId != null && { workspaceId }),
    ...(workspaceScope != null && { scope: workspaceScope }),
  };
  const resource = buildResourceContext('cycle', cycleId, normalizedCycleData);
  if (typeof process !== 'undefined' && process.env?.AUTHZ_RESOURCE_DEBUG === '1') {
    console.log('[AUTH RESOURCE]', {
      type: 'cycle',
      id: cycleId,
      createdBy: resource.createdBy,
      assigneeId: (resource as { assigneeId?: string; shapingOwnerId?: string }).assigneeId ?? (resource as { shapingOwnerId?: string }).shapingOwnerId,
      workspaceId: (resource as { workspaceId?: string }).workspaceId,
      lifecycleState: (resource as { lifecycleState?: string }).lifecycleState,
      exit_status: (resource as { exit_status?: string; detailedState?: string }).exit_status ?? (resource as { detailedState?: string }).detailedState ?? 'active',
      detailedState: (resource as { detailedState?: string }).detailedState, // legacy
    });
  }
  const cyclePrereqs = buildCyclePrerequisites(cycleId, cycleData);
  const roundPrereqs = buildRoundProposalPrerequisites(cycleId, cycleData);
  let decisionLoopsIncomplete: boolean | undefined;
  if (action === 'process.prioritize') {
    decisionLoopsIncomplete = await fetchDecisionLoopsIncomplete(cycleId);
  }
  const mergedContext: Partial<EvaluationContext> = {
    ...context,
    prerequisites: {
      ...context?.prerequisites,
      ...cyclePrereqs,
      ...roundPrereqs,
      ...(typeof decisionLoopsIncomplete === 'boolean' && { decisionLoopsIncomplete }),
    },
  };
  return checkAuthorization(action, resource, mergedContext);
}

/**
 * Build prerequisites for session pool actions from sessionData when present.
 */
function buildSessionPrerequisites(sessionData: Record<string, unknown>): Partial<EvaluationContext>['prerequisites'] {
  const sessionPoolLocked =
    typeof sessionData.sessionPoolLocked === 'boolean'
      ? sessionData.sessionPoolLocked
      : typeof sessionData.pool_locked === 'boolean'
        ? sessionData.pool_locked
        : undefined;
  if (sessionPoolLocked === undefined) return undefined;
  return { sessionPoolLocked };
}

/**
 * Check policy for a session without throwing.
 *
 * Sets workspace scope on the resource so canSync() uses the correct scope
 * kind ('resource' / workspaceId) rather than DEFAULT_SCOPE ('organization' /
 * '*'). Without this, job roles assigned at workspace scope fail the
 * scope-kind equality check and produce MISSING_CAPABILITY.
 */
export function checkPolicyForSession(
  action: string,
  sessionId: string,
  sessionData: Record<string, unknown>,
  context?: Partial<EvaluationContext>
): PolicyDecision {
  const workspaceId =
    (sessionData.workspaceId as string | undefined) ??
    (sessionData.workspace_id as string | undefined) ??
    useTenantStore.getState().currentWorkspaceId ??
    getCurrentWorkspaceId() ??
    undefined;
  const workspaceScope =
    workspaceId != null
      ? { kind: 'resource' as const, id: workspaceId, parentScope: { kind: 'organization' as const, id: '*' } }
      : undefined;
  const normalizedSessionData = {
    ...sessionData,
    ...(workspaceId != null && { workspaceId }),
    ...(workspaceScope != null && { scope: workspaceScope }),
  };
  const resource = buildResourceContext('session', sessionId, normalizedSessionData);
  const sessionPrereqs = buildSessionPrerequisites(sessionData);
  const mergedContext: Partial<EvaluationContext> = {
    ...context,
    ...(sessionPrereqs && { prerequisites: { ...context?.prerequisites, ...sessionPrereqs } }),
  };
  return checkAuthorization(action, resource, mergedContext);
}

/**
 * Check if an item action is allowed (simple boolean).
 */
export function canPerformItemAction(
  action: string,
  itemId: string,
  itemData: Record<string, unknown>,
  context?: Partial<EvaluationContext>
): boolean {
  return checkPolicyForItem(action, itemId, itemData, context).allowed;
}

/**
 * Check if a list action is allowed (simple boolean).
 */
export function canPerformListAction(
  action: string,
  listId: string,
  listData: Record<string, unknown>,
  context?: Partial<EvaluationContext>
): boolean {
  return checkPolicyForList(action, listId, listData, context).allowed;
}

// ============================================================================
// Error Handling Utilities
// ============================================================================

/**
 * Handle an authorization error in a store mutation.
 * Returns a user-friendly error message and dispatches an event.
 */
export function handleAuthorizationError(error: unknown, storeName: string, action: string): string {
  if (isAuthorizationError(error)) {
    const message = getAuthorizationErrorMessage(error);
    
    // Dispatch event for UI notification
    window.dispatchEvent(new CustomEvent('authorization-denied', {
      detail: {
        store: storeName,
        action,
        message,
        decision: error.decision,
      }
    }));
    
    return message;
  }
  
  // Re-throw non-authorization errors
  throw error;
}

/**
 * Wrap a store mutation with authorization enforcement.
 * Returns null if authorized, or error message if denied.
 */
export function withAuthorization<T>(
  action: string,
  resource: ResourceContext,
  context: Partial<EvaluationContext> | undefined,
  mutation: () => T
): { result: T | null; error: string | null } {
  try {
    enforceAuthorization(action, resource, context);
    return { result: mutation(), error: null };
  } catch (error) {
    if (isAuthorizationError(error)) {
      return { result: null, error: getAuthorizationErrorMessage(error) };
    }
    throw error;
  }
}

// ============================================================================
// Context Builders for Common Scenarios
// ============================================================================

/**
 * Build evaluation context from a cycle/process.
 */
export function buildCycleContext(cycle: {
  phase?: string;
  submissionWindowOpen?: boolean;
  contributionsFrozen?: boolean;
}): Partial<EvaluationContext> {
  return {
    phase: cycle.phase,
    submissionWindowOpen: cycle.submissionWindowOpen ?? true,
    contributionsFrozen: cycle.contributionsFrozen ?? false,
  };
}

/**
 * Build evaluation context from a session.
 */
export function buildSessionContext(session: {
  state?: string;
  phase?: string;
}): Partial<EvaluationContext> {
  return {
    sessionState: session.state,
    phase: session.phase,
  };
}

// ============================================================================
// Batch Operations
// ============================================================================

/**
 * Check authorization for multiple items at once.
 * Returns a map of item IDs to decisions.
 */
export function checkPolicyForItems(
  action: string,
  items: Array<{ id: string; data: Record<string, unknown> }>,
  context?: Partial<EvaluationContext>
): Map<string, PolicyDecision> {
  const results = new Map<string, PolicyDecision>();
  
  for (const item of items) {
    const decision = checkPolicyForItem(action, item.id, item.data, context);
    results.set(item.id, decision);
  }
  
  return results;
}

/**
 * Filter items to only those where the action is allowed.
 */
export function filterAuthorizedItems<T extends { id: string }>(
  action: string,
  items: T[],
  getItemData: (item: T) => Record<string, unknown>,
  context?: Partial<EvaluationContext>
): T[] {
  return items.filter(item => {
    const decision = checkPolicyForItem(action, item.id, getItemData(item), context);
    return decision.allowed;
  });
}
